import { defineComponent as _defineComponent } from 'vue'
import { vModelCheckbox as _vModelCheckbox, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, vModelText as _vModelText, normalizeStyle as _normalizeStyle, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "edit-modal-content" }
const _hoisted_2 = {
  key: 0,
  class: "row1"
}
const _hoisted_3 = { class: "custom-control custom-checkbox mb0 mr16 mt6 update-ts-checkbox" }
const _hoisted_4 = { class: "row2 form-group" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "row3 form-group" }
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 1,
  class: "row3b form-group"
}
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "row4 form-group" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["value"]
const _hoisted_13 = {
  key: 0,
  class: "angle-arrow-wrap ml6"
}
const _hoisted_14 = { class: "row5 form-group" }
const _hoisted_15 = ["rows"]
const _hoisted_16 = { class: "row6" }
const _hoisted_17 = { class: "form-group errors" }

import { computed, onBeforeUnmount, onMounted, reactive } from 'vue';
import type { Comment } from 'src/models/comment';
import type { AdminCommentUpdateRequest } from 'src/models/apis/comment/adminCommentRequest';
import type { CandidateFile } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import {
  hasDamageRankFeature,
  getNearKps,
  validateAngle,
  validateComment,
  fetchFileAsObjectInfo,
  ATTACHMENT_FILE_MAX_MB_DEFAULT,
  MAX_SAVABLE_FILES_COUNT,
  ACCEPTED_FILE_TYPES,
} from 'src/lib/commentHelper';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import { splitByLineBreak } from 'src/lib/utils';
import { convertFilesToFileObjs, type FileObj } from 'src/lib/fileHelper';
import { useReactiveMasters } from 'src/composables/useMasters';
import PreviewFilesArea from 'src/components/lib/PreviewFilesArea/index.vue';
import { COMMENT_DAMAGE_RANK_CODES, type CommentDamageRank } from 'src/consts/comment_damage_rank';
import { COMMENT_SOURCE_TYPE_SELF } from 'src/consts/comment_source_type';

const NEAR_KP_THRESHOLD_METERS = 100;

interface CommentEditForm {
  commentType: string;
  content: string | null;
  shareScope: number | null;
  angle: number | null;
  update_ts: boolean;
  damageRank: CommentDamageRank | null;
  contentTextareaRows: number;
  kpUid: string | null;
  lat: string;
  lon: string;
  saveFiles: FileObj[];
  removeFileIds: number[];
}

const editModalTitle = '編集';


export default /*@__PURE__*/_defineComponent({
  __name: 'CommentEditModal',
  props: {
    editingComment: {},
    isRequesting: { type: Boolean },
    httpErrorMsgs: {}
  },
  emits: ["update", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const buildEditForm = (comment: Comment): CommentEditForm => {
  const minRows = 4;
  const rows = Math.max(splitByLineBreak(comment.content).length, minRows);
  return {
    commentType: comment.commentType,
    content: comment.content,
    contentTextareaRows: rows,
    shareScope: comment.shareScope,
    angle: comment.angle,
    kpUid: comment.kpUid,
    lat: comment.location.lat,
    lon: comment.location.lon,
    update_ts: false,
    damageRank: comment.damageRank,
    saveFiles: [],
    removeFileIds: [],
  };
};

const state = reactive<{ editing: CommentEditForm; candidateFiles: CandidateFile[]; errorMsgs: string[] }>({
  editing: buildEditForm(props.editingComment),
  candidateFiles: [],
  errorMsgs: [],
});

const { masters } = useReactiveMasters();

const errorMsgs = computed<string[]>(() => [...props.httpErrorMsgs, ...state.errorMsgs]);

const isSaveButtonDisabled = computed<boolean>(() => {
  return props.isRequesting || !validateComment(state.editing) || state.errorMsgs.length > 0;
});
const nearKps = computed<GIKp[]>(() => {
  if (!state.editing.lat || !state.editing.lon) return [];

  return getNearKps(
    { lat: state.editing.lat, lon: state.editing.lon },
    masters.kp.kpMap,
    masters.kp.roadNameDispMap,
    NEAR_KP_THRESHOLD_METERS,
  );
});
const editItemHasValidAngle = computed<boolean>(() => {
  return validateAngle(state.editing.angle);
});
const sortedPreviewFiles = computed<CandidateFile[]>(() => {
  return [...state.candidateFiles].sort((a, b) => a.id - b.id);
});
const canUpdateTs = computed<boolean>(() => {
  return props.editingComment.sourceType === COMMENT_SOURCE_TYPE_SELF;
});
const hasDamageRank = computed<boolean>(() => {
  return hasDamageRankFeature(masters.comment.commentFeaturesBySourceType, props.editingComment.sourceType);
});

const releaseAllFiles = (): void => {
  state.editing.saveFiles.forEach(elm => URL.revokeObjectURL(elm.content));
  state.candidateFiles.forEach(elm => URL.revokeObjectURL(elm.content));
};

const toPayload = (): AdminCommentUpdateRequest => {
  return {
    lat: state.editing.lat ?? '',
    lon: state.editing.lon ?? '',
    movie_id: null,
    share_scope: state.editing.shareScope || 0,
    comment_type: state.editing.commentType,
    content: state.editing.content || '',
    kp_uid: state.editing.kpUid || null,
    angle: state.editing.angle,
    update_ts: state.editing.update_ts,
    save_files: state.editing.saveFiles.map(e => e.file),
    remove_file_ids: state.editing.removeFileIds,
    damage_rank: state.editing.damageRank,
  };
};

const save = (): void => {
  if (isSaveButtonDisabled.value) return;

  emit('update', props.editingComment.id, toPayload());
};

const handleFileUpload = async(fileList: FileList): Promise<void> => {
  state.errorMsgs = [];
  const uploadFilesCount = fileList.length + state.candidateFiles.length;
  try {
    if (uploadFilesCount > MAX_SAVABLE_FILES_COUNT) {
      throw Error('添付ファイルの最大数は' + MAX_SAVABLE_FILES_COUNT + '個です。');
    }
    // 新規ファイルのidを連番で割り振る(実際のidはサーバー側で振られる)
    const maxId = state.candidateFiles.map(e => e.id).reduce((a, b) => Math.max(a, b), 0);
    const fileObjs = await convertFilesToFileObjs(fileList, maxId, ATTACHMENT_FILE_MAX_MB_DEFAULT);
    state.candidateFiles.push(...fileObjs);
    state.editing.saveFiles.push(...fileObjs);
  } catch (error) {
    state.errorMsgs.push(...getErrorMessages(error as AxiosError));
  }
};
const removeFile = (fileId: number): void => {
  state.candidateFiles = state.candidateFiles.filter(e => e.id !== fileId);
  const saveFile = state.editing.saveFiles.find(e => e.id === fileId);
  if (saveFile) {
    state.editing.saveFiles = state.editing.saveFiles.filter(e => e.id !== fileId);
    return;
  }
  state.editing.removeFileIds.push(fileId);
};

onMounted(async() => {
  const commentFilePromises = props.editingComment.commentFiles.map<Promise<CandidateFile | null>>(async(file) => {
    const savedFileInfo = await fetchFileAsObjectInfo(props.editingComment.id, file.id);
    if (!savedFileInfo || !savedFileInfo.url || !savedFileInfo.type) return null;

    return {
      id: file.id,
      content: savedFileInfo.url,
      type: savedFileInfo.type,
      name: file.fileName ?? '',
      path: file.filePath,
      selected: true,
    };
  });
  state.candidateFiles = (await Promise.all(commentFilePromises))
    .filter((e): e is CandidateFile => e !== null);
});

onBeforeUnmount(() => {
  releaseAllFiles();
});

return (_ctx: any,_cache: any) => {
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createBlock(_component_Modal, {
    title: editModalTitle,
    "bottom-close-button-label": "保存",
    isCloseButtonDisabled: isSaveButtonDisabled.value,
    onClose: save,
    onDismiss: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('close')))
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (canUpdateTs.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  id: "update_ts",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.editing.update_ts) = $event)),
                  class: "custom-control-input"
                }, null, 512), [
                  [_vModelCheckbox, state.editing.update_ts]
                ]),
                _cache[8] || (_cache[8] = _createElementVNode("label", {
                  class: "custom-control-label",
                  for: "update_ts"
                }, " 更新を通知 ", -1))
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { class: "form-control-label" }, "種別", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.editing.commentType) = $event))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(masters).comment.commentTypes, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.commentType,
                value: e.commentType
              }, _toDisplayString(e.commentTypeDisp), 9, _hoisted_5))
            }), 128))
          ], 512), [
            [_vModelSelect, state.editing.commentType]
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[10] || (_cache[10] = _createElementVNode("label", { class: "form-control-label" }, "公開範囲", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.editing.shareScope) = $event))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(masters).lov.shareScopes, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.key,
                value: e.key
              }, _toDisplayString(e.val), 9, _hoisted_7))
            }), 128))
          ], 512), [
            [_vModelSelect, state.editing.shareScope]
          ])
        ]),
        (hasDamageRank.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _cache[12] || (_cache[12] = _createElementVNode("label", { class: "form-control-label" }, "判定", -1)),
              _withDirectives(_createElementVNode("select", {
                class: "form-control",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((state.editing.damageRank) = $event))
              }, [
                _cache[11] || (_cache[11] = _createElementVNode("option", { value: null }, null, -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(COMMENT_DAMAGE_RANK_CODES), (code) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: code,
                    value: code
                  }, _toDisplayString(code), 9, _hoisted_9))
                }), 128))
              ], 512), [
                [_vModelSelect, state.editing.damageRank]
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_10, [
          _cache[15] || (_cache[15] = _createElementVNode("span", { class: "lbl ws-nowrap" }, "場所", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "custom-select form-control ml4 mr4",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.editing.kpUid) = $event)),
            disabled: nearKps.value.length === 0
          }, [
            _cache[13] || (_cache[13] = _createElementVNode("option", { value: "" }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nearKps.value, (e, idx) => {
              return (_openBlock(), _createElementBlock("option", {
                key: idx,
                value: e.kpUid
              }, _toDisplayString(e.locationDisp), 9, _hoisted_12))
            }), 128))
          ], 8, _hoisted_11), [
            [_vModelSelect, state.editing.kpUid]
          ]),
          _cache[16] || (_cache[16] = _createElementVNode("span", { class: "lbl ws-nowrap" }, "方角", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((state.editing.angle) = $event)),
            type: "number",
            min: "-180",
            max: "180",
            class: "ib form-control ta-right angle ml4"
          }, null, 512), [
            [_vModelText, state.editing.angle]
          ]),
          _cache[17] || (_cache[17] = _createElementVNode("span", { class: "ib ml2" }, "°", -1)),
          (editItemHasValidAngle.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "angle-arrow-bg" }, null, -1)),
                _createElementVNode("div", {
                  class: "angle-arrow-img",
                  style: _normalizeStyle({ transform: `rotateZ(${state.editing.angle}deg)`})
                }, null, 4)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_14, [
          _cache[18] || (_cache[18] = _createElementVNode("label", { class: "form-control-label" }, "内容", -1)),
          _withDirectives(_createElementVNode("textarea", {
            class: "form-control",
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((state.editing.content) = $event)),
            rows: state.editing.contentTextareaRows
          }, "          ", 8, _hoisted_15), [
            [_vModelText, state.editing.content]
          ])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createVNode(PreviewFilesArea, {
            "preview-files": sortedPreviewFiles.value,
            "is-changeable": true,
            "accepted-file-types": _unref(ACCEPTED_FILE_TYPES),
            onFileUpload: handleFileUpload,
            onRemoveFile: removeFile
          }, null, 8, ["preview-files", "accepted-file-types"])
        ]),
        _createElementVNode("div", _hoisted_17, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errorMsgs.value, (e, i) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "error",
              key: i
            }, _toDisplayString(e), 1))
          }), 128))
        ])
      ])
    ]),
    _: 1
  }, 8, ["isCloseButtonDisabled"]))
}
}

})